import { useCallback, useEffect, useRef, useState } from 'react'
import { supabase } from '../lib/supabase.js'

export const EMPTY_SUMMARY = {
  taxa_total: 0,
  taxa_approved: 0,
  characters_total: 0,
  characters_approved: 0,
  assertions_total: 0,
  assertions_approved: 0,
  review_queue: 0,
  sources_total: 0,
  specialist_groups: 0,
}

function normaliseSummary(data) {
  const source = Array.isArray(data) ? data[0] : data
  return Object.fromEntries(Object.keys(EMPTY_SUMMARY).map((key) => [
    key,
    Number.isFinite(Number(source?.[key])) ? Number(source[key]) : 0,
  ]))
}

export default function useDashboardSummary() {
  const mounted = useRef(true)
  const hasLoaded = useRef(false)
  const [summary, setSummary] = useState(EMPTY_SUMMARY)
  const [isLoading, setIsLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [error, setError] = useState('')
  const [lastRefreshed, setLastRefreshed] = useState(null)

  useEffect(() => {
    mounted.current = true
    return () => { mounted.current = false }
  }, [])

  const refresh = useCallback(async () => {
    const initial = !hasLoaded.current
    if (initial) setIsLoading(true)
    else setIsRefreshing(true)
    setError('')

    try {
      const { data, error: requestError } = await supabase.rpc('dashboard_summary')
      if (requestError) throw requestError
      if (!mounted.current) return
      setSummary(normaliseSummary(data))
      setLastRefreshed(new Date())
      hasLoaded.current = true
    } catch (requestError) {
      if (mounted.current) setError(requestError instanceof Error ? requestError.message : 'The dashboard summary could not be loaded.')
    } finally {
      if (mounted.current) {
        setIsLoading(false)
        setIsRefreshing(false)
      }
    }
  }, [])

  useEffect(() => { refresh() }, [refresh])

  return { summary, isLoading, isRefreshing, error, lastRefreshed, refresh, hasData: hasLoaded.current }
}
